import { useCallback, useEffect, useState, useRef } from "react";

import { useFireAnalyticsEvents } from "../../../../analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectIdKey,
    ActionSubjectKey
} from "../../../../analytics/constants";
import { NO_ORG_ID, useFastshiftSelector } from "../../../state";
import { useTicketsFilteredBySelectedOrg } from "../utils/use-tickets-filtered-by-selected-org";

import { BaseStatusTable } from "./base-status-table/base-status-table";
import {
    DEFAULT_INITIAL_NUMBER_OF_TICKETS,
    INCREMENT_TICKET_COUNT_BY
} from "./constants";
import { getTableRows } from "./utils/get-table-rows";

export const MigrationStatusTable = () => {
    const orgServers = useFastshiftSelector((state) => state.orgServers);
    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const isMigrationPlanTotalsPending = useFastshiftSelector(
        (state) => state.migrationPlanTotals.pending
    );
    const isL1AssessmentsPending = useFastshiftSelector(
        (state) => state.l1Assessments.pending
    );

    const { fireTrackEvent, fireUIEvent } = useFireAnalyticsEvents();

    const isOrgAdmin =
        useFastshiftSelector((state) => state.orgs).data.length > 1;

    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    // send track event once the empty migration summary section is loaded
    const loadStartRef = useRef<number>(Date.now());
    const hasSentTrackEvent = useRef<boolean>(false);
    const hasSentTrackEventForNoOrg = useRef<boolean>(false); // secondary check for no-org edge case
    const hasSentFirstTrackEventOnPageLoad = useRef<boolean>(false);

    const { filteredTickets, isPending, hasError, retry } =
        useTicketsFilteredBySelectedOrg();

    const [numberOfTicketsToShow, setNumberOfTicketsToShow] = useState(
        DEFAULT_INITIAL_NUMBER_OF_TICKETS
    );

    const handleClickShowMore = useCallback(() => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.BUTTON,
            actionSubjectId:
                ActionSubjectIdKey.FASTSHIFT_MIGRATION_PROGRESS_TABLE_SHOW_MORE_BUTTON_CLICK,
            source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {}
        });

        setNumberOfTicketsToShow(
            numberOfTicketsToShow + INCREMENT_TICKET_COUNT_BY
        );
    }, [numberOfTicketsToShow, fireUIEvent]);

    const handleSort = useCallback(
        (key: string) => {
            if (sortKey === key) {
                setSortDirection(sortDirection === "asc" ? "desc" : "asc");
            } else {
                setSortKey(key);
                setSortDirection("asc");
            }

            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.TABLE_COLUMN_SORT,
                actionSubjectId: ActionSubjectIdKey.MIGRATION_PROGRESS_TABLE,
                source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
                attributes: {
                    sortKey: key
                }
            });
        },
        [sortKey, fireUIEvent, sortDirection]
    );

    const { rows, rowsAnalyticsInfo: rowsAdditionalAnalyticsInfo } =
        getTableRows({
            moveTickets: filteredTickets,
            orgServers,
            sortKey,
            sortDirection
        });

    useEffect(() => {
        // This implies a change in selected org or return from API, so reset everything
        setNumberOfTicketsToShow(
            Math.min(rows.length, DEFAULT_INITIAL_NUMBER_OF_TICKETS)
        );
        setSortKey(null);
        setSortDirection("asc");
    }, [rows.length]);

    const isLoading =
        isPending || isMigrationPlanTotalsPending || isL1AssessmentsPending;

    useEffect(() => {
        if (isLoading && hasSentTrackEvent.current) {
            // Reset the load start time when loading starts
            loadStartRef.current = Date.now();
            hasSentTrackEvent.current = false;
            hasSentTrackEventForNoOrg.current = false;
        }
        // send track event when table is first loaded, or is re-loaded after a change in selected org or 'refresh' button click
        else if (
            !isLoading &&
            (!hasSentTrackEvent.current ||
                (selectedOrg?.id === NO_ORG_ID &&
                    !hasSentTrackEventForNoOrg.current))
        ) {
            const elapsed = Date.now() - loadStartRef.current;
            if (selectedOrg?.id === NO_ORG_ID) {
                hasSentTrackEventForNoOrg.current = true;
            }

            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectIdKey.MIGRATION_PROGRESS_TABLE,
                source: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
                attributes: {
                    firstLoad: !hasSentFirstTrackEventOnPageLoad.current,
                    loadTime:
                        hasSentFirstTrackEventOnPageLoad.current &&
                        selectedOrg?.id === NO_ORG_ID
                            ? null
                            : elapsed, // switching to 'no-org' org doesnt require load time
                    selectedOrgId: selectedOrg?.id,
                    tableHasShowMore: rows.length > numberOfTicketsToShow,
                    tableRowCount: rows.length,
                    hasError: hasError,

                    ...rowsAdditionalAnalyticsInfo
                }
            });

            hasSentTrackEvent.current = true;
            hasSentFirstTrackEventOnPageLoad.current = true;
        }
    }, [
        fireTrackEvent,
        hasError,
        isLoading,
        numberOfTicketsToShow,
        rows.length,
        selectedOrg?.id,
        rowsAdditionalAnalyticsInfo
    ]);

    return (
        <BaseStatusTable
            rows={rows}
            isLoading={isLoading}
            hasError={hasError}
            numberOfTicketsToShow={numberOfTicketsToShow}
            selectedOrg={selectedOrg}
            isOrgAdmin={isOrgAdmin}
            sortDirection={sortDirection}
            sortKey={sortKey}
            handleClickShowMore={handleClickShowMore}
            handleSort={handleSort}
            retryRequests={retry}
        />
    );
};
